import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import whatsappService from "../services/whatsappService";
import toast from "react-hot-toast";

const Profile: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRequestWhatsAppUpdate = async () => {
    setLoading(true);
    setError(null);

    try {
      await whatsappService.requestUpdate();
      toast.success(
        "Update link sent to your email. Please check your inbox and spam folder."
      );
    } catch (error: any) {
      setError(error.message);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">Please log in to view your profile.</Alert>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/login")}
            sx={{ mt: 2 }}
          >
            Login
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Profile
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Account Information
            </Typography>
            <Typography variant="body1">
              <strong>Username:</strong> {user.username}
            </Typography>
            <Typography variant="body1">
              <strong>Email:</strong> {user.email}
            </Typography>
            <Typography variant="body1">
              <strong>Role:</strong> {user.role}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              WhatsApp Number
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Update your WhatsApp number to allow buyers to contact you
              directly about your auctions.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRequestWhatsAppUpdate}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? "Sending Update Link..." : "Update WhatsApp Number"}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box>
            <Typography variant="h6" gutterBottom>
              My Auctions
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate("/my-pending-auctions")}
              sx={{ mr: 2 }}
            >
              View My Auctions
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/create")}
            >
              Create New Auction
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Profile;
